import { useState, useRef, useEffect } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import useGooglePlaces from '../../hooks/useGooglePlaces';

/**
 * Drop-in address input with Google Places autocomplete.
 * Falls back to a normal text input if the Maps API key is missing or fails
 * to load — never blocks the user from filling out the form.
 *
 * Props:
 *   value           — current text value
 *   onChange(text)  — called with the textbox string on every keystroke
 *   onSelect(addr)  — called when the user picks a suggestion (the resolved
 *                     address breakdown: {formatted, line1, city, state, postal, country, lat, lng})
 *   className       — applied to the underlying <input>
 *   inputProps      — spread onto the underlying <input> (e.g. data-testid)
 */
export default function AddressAutocompleteInput({ value, onChange, onSelect, className, inputProps }) {
  const { ready, error, predictions, search, resolve, clear } = useGooglePlaces();
  const [open, setOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const wrapRef = useRef(null);

  // Close dropdown on outside-click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    setOpen(true);
    if (ready) search(v);
  };

  const handlePick = async (p) => {
    setOpen(false);
    setResolving(true);
    try {
      const addr = await resolve(p.place_id);
      onChange(addr.formatted);
      onSelect && onSelect(addr);
      clear();
    } catch (e) {
      // fall through; user can still type manually
    } finally {
      setResolving(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500 pointer-events-none" />
        <input
          {...(inputProps || {})}
          type="text"
          autoComplete="off"
          value={value}
          onChange={handleChange}
          onFocus={() => { if (predictions.length) setOpen(true); }}
          placeholder={inputProps?.placeholder || 'Start typing your address…'}
          className={`${className || ''} pl-9`}
        />
        {resolving && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />}
      </div>

      {open && ready && predictions.length > 0 && (
        <ul
          data-testid="address-suggestions"
          className="absolute z-30 left-0 right-0 mt-1 bg-white dark:bg-[#161616] border border-gray-200 dark:border-white/[0.08] rounded-lg shadow-lg max-h-64 overflow-y-auto"
        >
          {predictions.map((p) => (
            <li key={p.place_id}>
              <button
                type="button"
                data-testid={`address-suggestion-${p.place_id}`}
                onClick={() => handlePick(p)}
                className="w-full text-left px-3 py-2 text-sm text-gray-800 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-500/10 flex items-start gap-2"
              >
                <MapPin className="w-3.5 h-3.5 mt-0.5 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                <span className="truncate">{p.description}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
          Address autocomplete unavailable. You can still type manually.
        </p>
      )}
    </div>
  );
}
